import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, UserPlus, PackagePlus, PackageMinus, BarChart2, Briefcase, Wrench, Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const HomePage = () => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 }
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <motion.div
      className="text-center py-6"
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
    >
      {/* Botón para volver a departamentos */}
      <div className="flex justify-start mb-4 max-w-7xl mx-auto px-4">
        <Link to="/">
          <motion.button
            className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Departamentos
          </motion.button>
        </Link>
      </div>

      <motion.div
        className="mb-8"
        variants={cardVariants}
      >
        <img 
          src="/images/REFACCIONARIA-V3.0.png" 
          alt="Refaccionaria V3.0" 
          className="mx-auto max-w-xs w-full h-auto object-contain"
        />
      </motion.div>
      <motion.h2
        className="text-4xl font-extrabold text-gray-800 mb-6 bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent"
        variants={cardVariants}
      >
        Bienvenido a tu Sistema De Almacen
      </motion.h2>
      <motion.p
        className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto"
        variants={cardVariants}
      >
        Aquí es donde la magia del inventario sucede. O al menos, donde intentamos que no pierdas la cabeza buscando cosas.
      </motion.p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8 max-w-7xl mx-auto">
        {/* Tarjeta de Inventario */}
        <Link to="/inventory">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <Package className="w-16 h-16 text-blue-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Inventario</h3>
            <p className="text-gray-500 text-center text-sm">Controla cada artículo, desde el más grande hasta el más insignificante.</p>
          </motion.div>
        </Link>

        {/* Tarjeta de Proveedores */}
        <Link to="/suppliers">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <UserPlus className="w-16 h-16 text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Proveedores</h3>
            <p className="text-gray-500 text-center text-sm">Mantén un registro de quién te trae las cosas y cuándo.</p>
          </motion.div>
        </Link>

        {/* Tarjeta de Entradas */}
        <Link to="/entries">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <PackagePlus className="w-16 h-16 text-cyan-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Entradas</h3>
            <p className="text-gray-500 text-center text-sm">Registra todo el material que llega a tu almacén.</p>
          </motion.div>
        </Link>

        {/* Tarjeta de Salidas */}
        <Link to="/exits">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <PackageMinus className="w-16 h-16 text-pink-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Salidas</h3>
            <p className="text-gray-500 text-center text-sm">Controla el material que sale y a dónde va.</p>
          </motion.div>
        </Link>

        {/* Nueva Tarjeta de Resumen General */}
        <Link to="/summary">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <BarChart2 className="w-16 h-16 text-purple-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Resumen General</h3>
            <p className="text-gray-500 text-center text-sm">Visualiza los KPIs clave de tu inventario.</p>
          </motion.div>
        </Link>

        {/* Tarjeta de Flotilla (abre menú de sub-tarjetas) */}
        <div className="relative">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            role="button"
            tabIndex={0}
            onClick={() => setMenuOpen(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMenuOpen(true); }}
          >
            <img src="/images/tractocamion.png" alt="Flotilla" className="w-16 h-16 mb-4 object-contain" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Flotilla</h3>
            <p className="text-gray-500 text-center text-sm">Gestiona las unidades de transporte de la empresa.</p>
          </motion.div>

          {/* Overlay menu */}
          {menuOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
              <motion.div
                className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-3xl mx-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Flotilla - Tipos de unidades</h3>
                  <button className="text-gray-500 hover:text-gray-800" onClick={() => setMenuOpen(false)} aria-label="Cerrar menu">✕</button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {/* Tractocamiones */}
                  <button
                    className="bg-gray-50 hover:bg-gray-100 rounded-lg p-4 flex flex-col items-center text-center border border-gray-100"
                    onClick={() => { setMenuOpen(false); navigate('/trucks/tractocamiones'); }}
                  >
                    <img src="/images/tractocamion.png" alt="Tractocamiones" className="w-10 h-10 mb-2 object-contain" />
                    <span className="text-sm font-medium">Tractocamiones</span>
                  </button>

                  {/* Auto Tanques */}
                  <button
                    className="bg-gray-50 hover:bg-gray-100 rounded-lg p-4 flex flex-col items-center text-center border border-gray-100"
                    onClick={() => { setMenuOpen(false); navigate('/trucks/auto-tanques'); }}
                  >
                    <img src="/images/auto_tanque.png" alt="Auto Tanques" className="w-10 h-10 mb-2 object-contain" />
                    <span className="text-sm font-medium">Auto Tanques</span>
                  </button>

                  {/* Dollys */}
                  <button
                    className="bg-gray-50 hover:bg-gray-100 rounded-lg p-4 flex flex-col items-center text-center border border-gray-100"
                    onClick={() => { setMenuOpen(false); navigate('/trucks/dollys'); }}
                  >
                    <img src="/images/dolly.png" alt="Dollys" className="w-10 h-10 mb-2 object-contain" />
                    <span className="text-sm font-medium">Dollys</span>
                  </button>

                  {/* Caja Seca */}
                  <button
                    className="bg-gray-50 hover:bg-gray-100 rounded-lg p-4 flex flex-col items-center text-center border border-gray-100"
                    onClick={() => { setMenuOpen(false); navigate('/trucks/caja-seca'); }}
                  >
                    <img src="/images/caja_seca.png" alt="Caja Seca" className="w-10 h-10 mb-2 object-contain" />
                    <span className="text-sm font-medium">Caja Seca</span>
                  </button>

                  {/* unidad de gasolina */}
                  <button
                    className="bg-gray-50 hover:bg-gray-100 rounded-lg p-4 flex flex-col items-center text-center border border-gray-100"
                    onClick={() => { setMenuOpen(false); navigate('/trucks/unidades-gas'); }}
                  >
                    <img src="/images/unidades_gas.png" alt="unidad de gasolina" className="w-10 h-10 mb-2 object-contain" />
                    <span className="text-sm font-medium">unidad de gasolina</span>
                  </button>

                  {/* Semiremolques */}
                  <button
                    className="bg-gray-50 hover:bg-gray-100 rounded-lg p-4 flex flex-col items-center text-center border border-gray-100"
                    onClick={() => { setMenuOpen(false); navigate('/trucks/semiremolques'); }}
                  >
                    <img src="/images/semiremolque.png" alt="Semiremolques" className="w-10 h-10 mb-2 object-contain" />
                    <span className="text-sm font-medium">Semiremolques</span>
                  </button>

                  {/* Gruas */}
                  <button
                    className="bg-gray-50 hover:bg-gray-100 rounded-lg p-4 flex flex-col items-center text-center border border-gray-100"
                    onClick={() => { setMenuOpen(false); navigate('/trucks/gruas'); }}
                  >
                    <img src="/images/grua.png" alt="Gruas" className="w-10 h-10 mb-2 object-contain" />
                    <span className="text-sm font-medium">Gruas</span>
                  </button>

                  {/* Estado Flota */}
                  <button
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg p-4 flex flex-col items-center text-center border border-blue-200"
                    onClick={() => { setMenuOpen(false); navigate('/fleet-status'); }}
                  >
                    <BarChart2 className="w-10 h-10 mb-2 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">Estado Flota</span>
                  </button>

                  {/* Programa de Mantenimientos */}
                  <button
                    className="bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-lg p-4 flex flex-col items-center text-center border border-purple-200"
                    onClick={() => { setMenuOpen(false); navigate('/maintenance-schedule'); }}
                  >
                    <Calendar className="w-10 h-10 mb-2 text-purple-600" />
                    <span className="text-sm font-medium text-purple-900">Prog. Mantenimientos</span>
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </div>

        {/* Tarjeta de Préstamos */}
        <Link to="/loans">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <Briefcase className="w-16 h-16 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Préstamos</h3>
            <p className="text-gray-500 text-center text-sm">Controla las piezas prestadas y su devolución.</p>
          </motion.div>
        </Link>

        {/* Tarjeta de Mantenimientos */}
        <Link to="/maintenances">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <Wrench className="w-16 h-16 text-yellow-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Mantenimientos</h3>
            <p className="text-gray-500 text-center text-sm">Gestiona los mantenimientos de la flotilla.</p>
          </motion.div>
        </Link>

        {/* Tarjeta de Fleet Tracking */}
        <Link to="/fleet-tracking">
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-8 h-64 flex flex-col items-center justify-center border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <MapPin className="w-16 h-16 text-emerald-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Fleet Tracking</h3>
            <p className="text-gray-500 text-center text-sm">Registro diario de tractocamiones en patio y talleres.</p>
          </motion.div>
        </Link>


      </div>
    </motion.div>
  );
};

export default HomePage;